import * as THREE from 'three';
import type { Vec3Lit } from '../levels/types';
import { clamp } from '../util/math';

export interface Frame {
  /** Normalized arc-length position 0..1. */
  t: number;
  pos: THREE.Vector3;
  tangent: THREE.Vector3; // unit, points downhill
  right: THREE.Vector3; // unit, +lateral direction
  up: THREE.Vector3; // unit surface normal (banked)
  halfWidth: number;
  bank: number;
}

export interface Projection {
  t: number;
  /** Signed lateral distance from centerline, world units (+ = right). */
  lateral: number;
  /** lateral / halfWidth, so |value| > 1 means off the ribbon. */
  lateralNorm: number;
  /** Height of the query point above the (banked) surface. */
  height: number;
  surfacePoint: THREE.Vector3;
  frame: Frame;
}

interface Sample {
  pos: THREE.Vector3;
  tangent: THREE.Vector3;
  right: THREE.Vector3;
  up: THREE.Vector3;
  halfWidth: number;
  bank: number;
  cum: number; // cumulative arc length
}

const WORLD_UP = new THREE.Vector3(0, 1, 0);

/**
 * Arc-length parameterized Catmull-Rom track centerline with a banked frame.
 * Provides fast nearest-point projection for surface/respawn queries.
 */
export class TrackSpline {
  readonly length: number;
  private samples: Sample[] = [];
  private curve: THREE.CatmullRomCurve3;

  constructor(
    controlPoints: Vec3Lit[],
    halfWidths: number[],
    banks: number[] | undefined,
    resolution = 24,
  ) {
    const pts = controlPoints.map((p) => new THREE.Vector3(p.x, p.y, p.z));
    this.curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.5);

    const cpCount = controlPoints.length;
    const n = Math.max(2, (cpCount - 1) * resolution);

    // First pass: dense uniform-u sampling with a parallel-transport frame so the
    // up vector stays continuous through steep / twisty sections.
    let prevRight: THREE.Vector3 | null = null;
    const raw: Sample[] = [];
    for (let i = 0; i <= n; i++) {
      const u = i / n;
      const pos = this.curve.getPoint(u);
      const tangent = this.curve.getTangent(u).normalize();

      let right = new THREE.Vector3().crossVectors(tangent, WORLD_UP);
      if (right.lengthSq() < 1e-6) {
        // Near-vertical tangent: fall back to previous right.
        right = prevRight ? prevRight.clone() : new THREE.Vector3(1, 0, 0);
      }
      right.normalize();
      // Keep handedness consistent with previous sample (avoid sudden flips).
      if (prevRight && right.dot(prevRight) < 0) right.multiplyScalar(-1);
      prevRight = right.clone();

      const up = new THREE.Vector3().crossVectors(right, tangent).normalize();

      // Interpolate authored half-width / bank by control-point parameter.
      const cpF = u * (cpCount - 1);
      const i0 = Math.min(cpCount - 1, Math.floor(cpF));
      const i1 = Math.min(cpCount - 1, i0 + 1);
      const f = cpF - i0;
      const halfWidth = THREE.MathUtils.lerp(halfWidths[i0], halfWidths[i1], f);
      const bank = banks ? THREE.MathUtils.lerp(banks[i0], banks[i1], f) : 0;

      // Apply banking: rotate right & up around the tangent.
      if (bank !== 0) {
        const q = new THREE.Quaternion().setFromAxisAngle(tangent, bank);
        right.applyQuaternion(q).normalize();
        up.applyQuaternion(q).normalize();
      }

      raw.push({ pos, tangent, right, up, halfWidth, bank, cum: 0 });
    }

    // Cumulative arc length.
    let total = 0;
    raw[0].cum = 0;
    for (let i = 1; i < raw.length; i++) {
      total += raw[i].pos.distanceTo(raw[i - 1].pos);
      raw[i].cum = total;
    }
    this.length = total;
    this.samples = raw;
  }

  private lerpSamples(a: Sample, b: Sample, f: number, cum: number): Frame {
    const pos = a.pos.clone().lerp(b.pos, f);
    const tangent = a.tangent.clone().lerp(b.tangent, f).normalize();
    const right = a.right.clone().lerp(b.right, f).normalize();
    const up = a.up.clone().lerp(b.up, f).normalize();
    return {
      t: this.length > 0 ? cum / this.length : 0,
      pos,
      tangent,
      right,
      up,
      halfWidth: THREE.MathUtils.lerp(a.halfWidth, b.halfWidth, f),
      bank: THREE.MathUtils.lerp(a.bank, b.bank, f),
    };
  }

  /** Frame at normalized arc-length t (0..1). */
  frameAt(t: number): Frame {
    const target = clamp(t, 0, 1) * this.length;
    // Binary search cumulative length.
    let lo = 0;
    let hi = this.samples.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.samples[mid].cum < target) lo = mid + 1;
      else hi = mid;
    }
    const i1 = clamp(lo, 1, this.samples.length - 1);
    const i0 = i1 - 1;
    const a = this.samples[i0];
    const b = this.samples[i1];
    const seg = b.cum - a.cum;
    const f = seg > 1e-6 ? (target - a.cum) / seg : 0;
    return this.lerpSamples(a, b, f, target);
  }

  /** World position at (t, lateralNorm in -1..1, height above surface). */
  pointAt(t: number, lateralNorm: number, height = 0): THREE.Vector3 {
    const fr = this.frameAt(t);
    return fr.pos
      .clone()
      .addScaledVector(fr.right, lateralNorm * fr.halfWidth)
      .addScaledVector(fr.up, height);
  }

  frameCount(): number {
    return this.samples.length;
  }

  /**
   * Nearest-point projection. `hintIndex` (a previous result's sampleIndex)
   * narrows the search for per-frame queries on a moving ball.
   */
  project(point: THREE.Vector3, hintIndex = -1): Projection & { sampleIndex: number } {
    let bestI = 0;
    let bestD = Infinity;

    const search = (from: number, to: number) => {
      for (let i = from; i <= to; i++) {
        const d = this.samples[i].pos.distanceToSquared(point);
        if (d < bestD) {
          bestD = d;
          bestI = i;
        }
      }
    };

    if (hintIndex >= 0) {
      const window = 40;
      const from = Math.max(0, hintIndex - window);
      const to = Math.min(this.samples.length - 1, hintIndex + window);
      search(from, to);
      // If we hit the edge of the window the ball may have teleported; widen.
      if (bestI === from || bestI === to) search(0, this.samples.length - 1);
    } else {
      search(0, this.samples.length - 1);
    }

    // Refine between neighbours by projecting onto the local tangent.
    const i0 = Math.max(0, bestI - 1);
    const i1 = Math.min(this.samples.length - 1, bestI + 1);
    let chosenA = bestI;
    let chosenF = 0;
    let chosenDist = bestD;
    for (const j of [i0, i1]) {
      if (j === bestI) continue;
      const a = this.samples[Math.min(bestI, j)];
      const b = this.samples[Math.max(bestI, j)];
      const ab = b.pos.clone().sub(a.pos);
      const len2 = ab.lengthSq();
      if (len2 < 1e-9) continue;
      const f = clamp(point.clone().sub(a.pos).dot(ab) / len2, 0, 1);
      const proj = a.pos.clone().addScaledVector(ab, f);
      const d = proj.distanceToSquared(point);
      if (d < chosenDist) {
        chosenDist = d;
        chosenA = Math.min(bestI, j);
        chosenF = f;
      }
    }

    const a = this.samples[chosenA];
    const b = this.samples[Math.min(this.samples.length - 1, chosenA + 1)];
    const cum = THREE.MathUtils.lerp(a.cum, b.cum, chosenF);
    const frame = this.lerpSamples(a, b, chosenF, cum);

    const rel = point.clone().sub(frame.pos);
    const lateral = rel.dot(frame.right);
    const height = rel.dot(frame.up);
    const surfacePoint = frame.pos.clone().addScaledVector(frame.right, lateral);

    return {
      t: frame.t,
      lateral,
      lateralNorm: frame.halfWidth > 0 ? lateral / frame.halfWidth : 0,
      height,
      surfacePoint,
      frame,
      sampleIndex: chosenA,
    };
  }
}
